/**
 * Locale-aware field selection for translated content.
 *
 * The backend stores pt-BR translations alongside the English originals
 * (`title_br`, `overview_br`, `name_br`) and `@shared/mappers` already carries
 * them into the domain types (`Title.titleBr` / `synopsisBr`, `Episode.titleBr`,
 * `Season.nameBr`, `CalendarEntry.titleBr` / `episodeTitleBr`). These helpers
 * pick the right variant for the active web locale, falling back to English
 * when the translation is missing or the locale is 'en' — a port of the
 * mobile `app/src/data/localized.ts`, typed against web's `Locale`.
 */

import type { Locale } from './locale';

/** Pick the localized variant of a string field, falling back to the primary. */
export function localized(primary: string, br: string | undefined, locale: Locale): string {
  return locale === 'pt-BR' && br ? br : primary;
}

/** Optional variant — returns undefined when the primary is also undefined. */
export function localizedOptional(
  primary: string | undefined,
  br: string | undefined,
  locale: Locale,
): string | undefined {
  if (!primary) return primary;
  return locale === 'pt-BR' && br ? br : primary;
}

/** TMDB genre names arrive in English; map the catalog's set to pt-BR. */
export const GENRES_PT: Record<string, string> = {
  Action: 'Ação',
  Adventure: 'Aventura',
  'Action & Adventure': 'Ação e Aventura',
  Animation: 'Animação',
  Comedy: 'Comédia',
  Crime: 'Crime',
  Documentary: 'Documentário',
  Drama: 'Drama',
  Family: 'Família',
  Fantasy: 'Fantasia',
  History: 'História',
  Horror: 'Terror',
  Kids: 'Infantil',
  Music: 'Música',
  Mystery: 'Mistério',
  News: 'Notícias',
  Reality: 'Reality',
  Romance: 'Romance',
  'Science Fiction': 'Ficção Científica',
  'Sci-Fi & Fantasy': 'Ficção Científica e Fantasia',
  Soap: 'Novela',
  Talk: 'Talk Show',
  'TV Movie': 'Filme para TV',
  Thriller: 'Suspense',
  War: 'Guerra',
  'War & Politics': 'Guerra e Política',
  Western: 'Faroeste',
};

/** Localize a catalog genre name, falling back to the English original. */
export function localizedGenre(name: string, locale: Locale): string {
  return locale === 'pt-BR' ? (GENRES_PT[name] ?? name) : name;
}

/** TMDB marks voice roles with "(voice)"; render the pt-BR equivalent. */
export function localizedRole(role: string, locale: Locale): string {
  return locale === 'pt-BR' ? role.replace('(voice)', '(voz)') : role;
}
